/** @format */

import {
	AfterViewInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	Input,
	OnDestroy,
	OnInit,
	PLATFORM_ID,
	ViewChild,
	computed,
	inject
} from '@angular/core';
import { CategoryDetail, CategoryDetails, FacetVO, ProductList } from '@plp/data-access/interfaces';
import { Chain, KillSwitches, ProductListSEO } from '@shared/data-access/interfaces';
import { DOCUMENT, KeyValue, NgTemplateOutlet, isPlatformBrowser } from '@angular/common';
import { HomefieldAccordionModule, HomefieldTextLinkModule } from '@homefield';
import {
	refreshProductList,
	selectFeatureFlags,
	selectHomrBannerData,
	selectProductListCategoryDetails,
	selectProductListFacetVOs,
	setMlBypass,
	setProductList
} from '@plp/data-access/state';
import { take, takeUntil } from 'rxjs/operators';

import { CarouselButtonComponent } from '../carousel-button/carousel-button.component';
import { CategoryBubbleComponent } from '../category-bubble/category-bubble.component';
import { HmfActionTextComponent } from '@dsg-tech/homefield-angular';
import { PlpBreadcrumbComponent } from '../breadcrumb/breadcrumb.component';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { UtilityService } from '@shared/utils/services';

@Component({
	selector: 'plp-components-product-list-header',
	standalone: true,
	imports: [
		CarouselButtonComponent,
		CategoryBubbleComponent,
		HomefieldAccordionModule,
		HomefieldTextLinkModule,
		PlpBreadcrumbComponent,
		NgTemplateOutlet,
		HmfActionTextComponent
	],
	templateUrl: './product-list-header.component.html',
	styleUrls: ['./product-list-header.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductListHeaderComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() killSwitches: KillSwitches;
	@Input() label = '';
	@Input() previous = false;
	@Input() productListData: ProductList | null = null;
	@Input() productListSEOData: ProductListSEO | null = null;
	@Input() isPlp3Template = false;

	@ViewChild('bubbleWrapper', { static: false }) bubbleWrapper: ElementRef<any>;

	private readonly UNSUBSCRIBE$ = new Subject<void>();
	private intersectionObserver: IntersectionObserver | null = null;
	private resizeObserver: ResizeObserver | null = null;
	private readonly document = inject(DOCUMENT);
	private readonly platformId = inject(PLATFORM_ID);

	readonly categoryDetails$ = this.store
		.select(selectProductListCategoryDetails)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly facetVOs$ = this.store
		.select(selectProductListFacetVOs)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly homrBannerData$ = this.store
		.select(selectHomrBannerData)
		.pipe(takeUntil(this.UNSUBSCRIBE$));
	readonly productListCategoryDetails$ = this.store
		.select(selectProductListCategoryDetails)
		.pipe(takeUntil(this.UNSUBSCRIBE$));

	readonly featureFlags = this.store.selectSignal(selectFeatureFlags);
	browseBreadcrumbsEnabled = computed(() => this.featureFlags().browseBreadcrumbsEnabled);
	shopAllCategoryBubbleEnabled = computed(() => this.featureFlags().shopAllCategoryBubbleEnabled);

	bubbleDetails: CategoryDetail[] | null = null;
	chainAbbr = this.chain.chainIdentifierAbbr;
	faceOutImage = '';
	faceOutImageIdentifier = '';
	salesAndDealsContent = false;
	scrollEnabled = false;
	showLeftArrow = false;
	showRightArrow = false;
	fuzzySearchResultHeader: string;
	constructor(
		private readonly utilityService: UtilityService,
		private readonly chain: Chain,
		private readonly changeRef: ChangeDetectorRef,
		private readonly store: Store
	) {}

	ngOnInit(): void {
		this.generateCategoryBubbles();

		this.homrBannerData$.subscribe((bannerData) => {
			if (bannerData && bannerData.templateName === 'sales-deals') {
				this.salesAndDealsContent = true;
			}
		});

		this.getSearchResultText();
	}

	ngAfterViewInit(): void {
		if (isPlatformBrowser(this.platformId) && this.bubbleWrapper?.nativeElement) {
			this.setupIntersectionObserver();
			this.setupResizeObserver();
			this.recalculateArrows();
		}
	}

	ngOnDestroy(): void {
		this.UNSUBSCRIBE$.next();
		this.UNSUBSCRIBE$.complete();

		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect();
			this.intersectionObserver = null;
		}

		if (this.resizeObserver) {
			this.resizeObserver.disconnect();
			this.resizeObserver = null;
		}
	}

	recalculateArrows(): void {
		const bubbleWrapper = this.bubbleWrapper.nativeElement;
		this.showLeftArrow = bubbleWrapper.scrollLeft >= 25;
		this.showRightArrow =
			bubbleWrapper.scrollLeft + bubbleWrapper.clientWidth <=
				bubbleWrapper.scrollWidth - 25 &&
			bubbleWrapper.scrollWidth > bubbleWrapper.offsetWidth;

		this.changeRef.detectChanges();
	}

	setupIntersectionObserver(): void {
		const bubbleWrapper = this.bubbleWrapper.nativeElement;

		this.intersectionObserver = new IntersectionObserver(
			(entries) => {
				if (entries?.[0]?.target === bubbleWrapper) {
					this.recalculateArrows();
				}
			},
			{
				root: null
			}
		);

		this.intersectionObserver.observe(bubbleWrapper);
	}

	setupResizeObserver(): void {
		const bubbleWrapper = this.bubbleWrapper.nativeElement;

		this.resizeObserver = new ResizeObserver(() => {
			this.recalculateArrows();
		});

		this.resizeObserver.observe(bubbleWrapper);
	}

	/** create shop by category carousel */
	generateCategoryBubbles(): void {
		this.facetVOs$.subscribe((facetVOs: FacetVO[]) => {
			if (!facetVOs || !facetVOs.length) return;

			const categoryBubblesFacets = facetVOs.filter(
				(facet) => facet.attrIdentifier === 'facetCatgroup'
			);
			// update when there is no category details to generate bubbles
			if (!categoryBubblesFacets.length) {
				this.bubbleDetails = [];
			} else {
				categoryBubblesFacets.forEach((facet) => {
					this.generateTiles(facet.valueList);
				});
			}
		});
	}

	getFaceoutThumbnailBaseUrl(chainId: string): string {
		if (chainId === 'dsg') {
			return 'https://www.dickssportinggoods.com/wcsstore/DicksSportingGoods/';
		}
		if (chainId === 'gg') {
			return 'https://www.golfgalaxy.com/wcsstore/GolfGalaxy/';
		}
		return 'https://www.dickssportinggoods.com/wcsstore/DicksSportingGoods/';
	}

	setBubbleThumbnail(thumbnail: string, topResultThumbnail: string): string {
		let faceOutImageIdentifier;
		if (thumbnail) {
			if (thumbnail.includes('Attachment/')) {
				// thumbnail field contains WCS url need to generate faceOutImage path for WCS host

				return this.getFaceoutThumbnailBaseUrl(this.chainAbbr) + thumbnail;
			} else {
				// thumbnail field isn't a WCS url generate as Akamai Image Storage path
				return `https://dks.scene7.com/is/image/dkscdn/${thumbnail}?$DSG_ProductCard$`;
			}
		} else if (topResultThumbnail) {
			// topResultThumbnail is used as a fallback when a manual override thumbnail isn't set this generates a Akamai Image Storage path for the api generated faceout image
			faceOutImageIdentifier = this.imagineUrlifyFaceOutImage(topResultThumbnail);
			return `https://dks.scene7.com/is/image/dkscdn/${faceOutImageIdentifier}?$DSG_ProductCard$`;
		}
	}

	/** create tiles of shop by category */
	generateTiles(facetValueList: { count: number; value: string }[]): void {
		const catBubbles: CategoryDetail[] = [];

		this.productListCategoryDetails$.pipe(take(1)).subscribe((catDetails: CategoryDetails) => {
			facetValueList.forEach((facetValue) => {
				if (facetValue && catDetails[facetValue.value]) {
					catBubbles.push({ ...catDetails[facetValue.value] });
				}
			});

			this.bubbleDetails = catBubbles;
			this.bubbleDetails.forEach((detail, index) => {
				this.bubbleDetails[index].imgSrc = this.setBubbleThumbnail(
					detail.thumbnail,
					detail.topResultThumbnail
				);
			});
			this.setBubbleDataEmAttribute();
		});
	}

	scrollToProductGrid(): void {
		event.preventDefault();
		const element = this.document.querySelector<HTMLElement>('#plp-srlp-content-wrapper');
		this.utilityService.scrollToElement(element, null, 50);
	}

	imagineUrlifyFaceOutImage(value: string): string {
		// This function accepts a string and first replaces any whitespace character with an underscore ( _ ).
		// Next the function looks for places where there are more than one underscore ( _ ) directly beside to each other it removes the extra underscores.
		return value.replace(/[^\w]/gi, '_').replace(/(_+)\1+/g, '$1');
	}

	searchInstead(): void {
		this.store.dispatch(setMlBypass({ mlBypass: true }));
		this.store.dispatch(
			setProductList({
				productList: {
					...this.productListData,
					searchVO: {
						...this.productListData.searchVO,
						searchTerm: this.productListData.didYouMeanVO?.originalSearchTerm
					}
				}
			})
		);
		this.store.dispatch(refreshProductList());
	}

	/** @description make sure key value pairs remain in order the way the api sends them when being looped through **/
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	originalOrder = (a: KeyValue<number, string>, b: KeyValue<number, string>): number => 0;

	getSearchResultText(): void {
		if (this.chainAbbr === 'gg') {
			this.fuzzySearchResultHeader = 'We Need a Mulligan!';
		} else if (this.chainAbbr === 'pl') {
			this.fuzzySearchResultHeader = 'No Luck! Keep Exploring.';
		} else {
			this.fuzzySearchResultHeader = 'We Struck Out';
		}
	}

	clickBubblesCarouselButton(isPrevious: boolean): void {
		const bubbleWrapper = document.getElementById('bubbles-wrapper');

		if (!bubbleWrapper) {
			return;
		}

		const newScrollLeft = isPrevious
			? bubbleWrapper.scrollLeft - 150
			: bubbleWrapper.scrollLeft + 150;

		bubbleWrapper.scrollTo({ left: newScrollLeft, behavior: 'smooth' });
	}

	private setBubbleDataEmAttribute(): void {
		if (!this.bubbleDetails || !this.productListSEOData) {
			return;
		}
		const seoName = this.productListSEOData?.name.replace(/['\s]/g, '');
		this.bubbleDetails.forEach((bubble, index) => {
			bubble.dataEmAttr = `LN_Bubble_${seoName}_${bubble?.name.replace(/['\s]/g, '')}_${index + 1}`;
		});
	}

	trackByFn(index: number, item: any): string {
		return `${index}-${item.value?.name}`;
	}
}
